import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { authService } from '../lib/auth';
import { toast } from 'sonner';

const AuthContext = createContext(null);

// Cierre de sesión automático tras 5 minutos de inactividad
const IDLE_TIMEOUT_MS = 5 * 60 * 1000;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const idleTimer = useRef(null);

  useEffect(() => {
    const currentUser = authService.getUser();
    setUser(currentUser);
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    const data = await authService.login(email, password);
    setUser(data.user);
    return data;
  };

  const logout = useCallback((reason) => {
    authService.logout();
    setUser(null);
    if (reason === 'idle') {
      toast.info('SalesPRO: Tu sesión se cerró por inactividad (5 minutos). Vuelve a iniciar sesión.');
    }
  }, []);

  // Auto-logout por inactividad: reinicia el contador con cualquier actividad del usuario
  useEffect(() => {
    if (!user) return;

    const reset = () => {
      if (idleTimer.current) clearTimeout(idleTimer.current);
      idleTimer.current = setTimeout(() => {
        logout('idle');
        window.location.href = '/login';
      }, IDLE_TIMEOUT_MS);
    };

    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'];
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    reset();

    return () => {
      events.forEach((e) => window.removeEventListener(e, reset));
      if (idleTimer.current) clearTimeout(idleTimer.current);
    };
  }, [user, logout]);

  const value = {
    user,
    login,
    logout,
    isAuthenticated: !!user,
    loading
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
